/**
 * lastUserCache.js
 *
 * Safe persistence for the `lastUser` AsyncStorage key.
 *
 * The key used to receive the FULL `/api/user/me` payload on every login and
 * every background data refresh. On stores whose user document has grown large
 * (1 MB+ serialized) that write crashed the web build with
 * `QuotaExceededError: Failed to execute 'setItem' on 'Storage'` (Sentry
 * HAPPY-APP-1D) — browser localStorage holds ~5 MB TOTAL across all keys, and
 * the giant user JSON competed with the foods/orders caches for it.
 *
 * Consumers only ever read a handful of identity fields from `lastUser`
 * (WhatsNewModal → roles; Avatar-style helpers → name/photo variants), so we
 * persist a slim projection instead of the raw payload, and every write is
 * try/catch-guarded: a failed cache write of a *convenience* key must never
 * take down login or the background refresh.
 *
 * @module helpers/lastUserCache
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Fields preserved in the persisted `lastUser` projection. Union of everything
 * any reader touches:
 *  - `Avatar.resolvePhotoUrl`: photo, avatarUrl, avatar_url, avatar, image
 *  - display-name helpers: name (object or string), firstName, lastName,
 *    username, userName, email
 *  - `WhatsNewModal` internal-entry gate: roles
 * @type {string[]}
 */
export const LAST_USER_FIELDS = [
    '_id',
    'name',
    'firstName',
    'lastName',
    'username',
    'userName',
    'email',
    'phone',
    'photo',
    'avatarUrl',
    'avatar_url',
    'avatar',
    'image',
    'roles',
];

/**
 * Project a full user payload down to the fields `lastUser` readers use.
 *
 * @param {object|null|undefined} user Full user object (e.g. `/api/user/me`).
 * @returns {object|null} Slim projection, or null for non-object input.
 */
export const slimLastUser = (user) => {
    if (!user || typeof user !== 'object') {
        return null;
    }

    return LAST_USER_FIELDS.reduce((acc, field) => {
        if (typeof user[field] !== 'undefined') {
            acc[field] = user[field];
        }

        return acc;
    }, {});
};

/**
 * Persist the slim `lastUser` projection. Never throws: a quota / storage
 * failure on this convenience key is logged and swallowed so the calling flow
 * (login, background refresh) always completes.
 *
 * @param {object|null|undefined} user Full user object to slim and store.
 * @returns {Promise<boolean>} true when the write succeeded.
 */
export const persistLastUser = async (user) => {
    const slim = slimLastUser(user);

    if (!slim) {
        return false;
    }

    try {
        await AsyncStorage.setItem('lastUser', JSON.stringify(slim));

        return true;
    } catch (error) {
        console.warn('[lastUserCache] persisting lastUser failed (non-fatal):', error?.message || error);

        return false;
    }
};

export default persistLastUser;
